import { useEffect, useRef } from "react";
import {
  ReferenceFieldBinding,
  useDataSource,
  type EntityReference,
  type FieldProps,
} from "@firecms/core";

import type { Event } from "../collections/events";
import { spacesCollection, type Space } from "../collections/spaces";
import {
  buildEventSuggestionsFromSpace,
  shouldApplySuggestion,
  type EventFieldSuggestions,
} from "./suggestEventFieldsFromSpace";

/**
 * Learning Path reference field that, on create, suggests Event name/title/body/logo
 * from the selected Space (without overwriting user edits).
 */
export function SuggestEventFieldsFromSpaceField(
  props: FieldProps<EntityReference, unknown, Event>
) {
  const { value, context } = props;
  const { fetchEntity } = useDataSource();
  const lastSuggestedRef = useRef<EventFieldSuggestions | null>(null);
  const valuesRef = useRef(context.values);
  valuesRef.current = context.values;

  const spaceId =
    value &&
    typeof value === "object" &&
    "isEntityReference" in value &&
    value.isEntityReference?.()
      ? value.id
      : undefined;
  const spacePath =
    value &&
    typeof value === "object" &&
    "isEntityReference" in value &&
    value.isEntityReference?.()
      ? value.path
      : undefined;

  const { status, setFieldValue } = context;

  useEffect(() => {
    if (status !== "new") {
      return;
    }
    if (!spaceId || !spacePath) {
      return;
    }

    let cancelled = false;

    void fetchEntity<Space>({
      path: spacePath,
      collection: spacesCollection,
      entityId: spaceId,
    }).then((entity) => {
      if (cancelled || !entity?.values) {
        return;
      }

      const suggestions = buildEventSuggestionsFromSpace(entity.values);
      const current = valuesRef.current;
      const previous = lastSuggestedRef.current;

      if (shouldApplySuggestion(current.name, previous?.name)) {
        setFieldValue("name", suggestions.name);
      }
      if (shouldApplySuggestion(current.title, previous?.title)) {
        setFieldValue("title", suggestions.title);
      }
      if (shouldApplySuggestion(current.body, previous?.body)) {
        setFieldValue("body", suggestions.body);
      }
      if (shouldApplySuggestion(current.logo, previous?.logo)) {
        setFieldValue("logo", suggestions.logo);
      }

      lastSuggestedRef.current = suggestions;
    });

    return () => {
      cancelled = true;
    };
  }, [fetchEntity, setFieldValue, spaceId, spacePath, status]);

  return <ReferenceFieldBinding {...props} />;
}
